
import { Z_INDEX } from '@/lib/constants/z-index'
import { useSidebarStore } from '@/stores/sidebar-store'
import { Link, useRouterState } from '@tanstack/react-router'
import {
  BarChart3,
  BookOpen,
  Bot,
  Briefcase,
  Building2,
  Calendar,
  ChevronsLeft,
  ChevronsRight,
  ClipboardList,
  Clock,
  FileSpreadsheet,
  FileText,
  GraduationCap,
  Heart,
  Home,
  MessageCircle,
  MessageSquare,
  Palette,
  Plane,
  Radar,
  Settings,
  Target,
  TrendingUp,
  Trophy,
  UserCheck,
  Users,
  UsersRound,
  Receipt,
  FileSignature,
  HeartHandshake,
  X
} from 'lucide-react'
import React from 'react'
import { User } from '../types/auth'
import UserInfo from './UserInfo'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface NavItem {
  name: string
  href: string
  icon: React.ComponentType<{ size?: string | number; className?: string; strokeWidth?: string | number }>
  roles: string[]
  comingSoon?: boolean // Indicates if this feature is under construction
  badge?: string // Optional badge label (e.g., "Em Teste")
}

interface NavSection {
  title: string
  items: NavItem[]
  roles: string[]
}

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
  user: User | null
  onJanesChatbotOpen: () => void
}

// Organized by user journey and information architecture
const navigationSections: NavSection[] = [
  {
    title: 'Início',
    roles: ['admin', 'diretor-executivo', 'diretor-financeiro', 'vice-presidente', 'diretor-tecnico', 'coordenador', 'ceo'],
    items: [
      {
        name: 'Big Numbers',
        href: '/big-numbers',
        icon: Home,
        roles: ['ceo', 'vice-presidente', 'diretor-financeiro']
      },
      {
        name: 'Dashboard',
        href: '/dashboard',
        icon: Home,
        roles: ['admin', 'diretor-executivo', 'diretor-financeiro', 'diretor-tecnico', 'coordenador', 'rh', 'analista-rh']
      }
    ]
  },
  {
    title: 'Resultados',
    roles: ['admin'], // Only admin can see Resultados
    items: [
      {
        name: 'Performance PROA',
        href: '/results',
        icon: Target,
        roles: ['admin'] // Only admin
      },
      {
        name: 'Minhas Metas',
        href: '/my-business-targets',
        icon: TrendingUp,
        roles: ['admin'] // Only admin
      }
    ],
  },
  {
    title: 'Financeiro',
    roles: ['admin', 'diretor-executivo', 'diretor-financeiro', 'vice-presidente', 'ceo'],
    items: [
      {
        name: 'Treinamentos',
        href: '/training',
        icon: GraduationCap,
        roles: ['admin']
      },
      {
        name: 'Performance Clientes',
        href: '/financial/performance-clientes',
        icon: TrendingUp,
        roles: ['admin', 'ceo', 'vice-presidente', 'diretor-executivo', 'diretor-financeiro']
      },
      {
        name: 'Central de Notas',
        href: '/financial/central-de-notas',
        icon: Receipt,
        roles: ['admin', 'ceo', 'vice-presidente', 'diretor-executivo']
      },
      {
        name: 'DRE',
        href: '/financial/dre',
        icon: FileText,
        roles: ['admin', 'ceo', 'vice-presidente', 'diretor-executivo', 'diretor-financeiro']
      },
      {
        name: 'Matriz de Clientes',
        href: '/financial/client-matrix',
        icon: BarChart3,
        roles: ['admin', 'ceo', 'vice-presidente', 'diretor-executivo', 'diretor-financeiro']
      },
      {
        name: 'PROA',
        href: '/financial/proa',
        icon: Target,
        roles: ['ceo', 'vice-presidente']
      },
      {
        name: 'Design System',
        href: '/design-system',
        icon: Palette,
        roles: ['admin']
      },
      {
        name: 'Configuração IA',
        href: '/ai-config',
        icon: Bot,
        roles: ['admin']
      },
      {
        name: 'Matriz de BU',
        href: '/financial/bu-matrix',
        icon: BarChart3,
        roles: ['ceo', 'vice-presidente'],
        badge: 'Em Teste'
      },
      {
        name: 'Gerencial',
        href: '/financial/gerencial',
        icon: FileSpreadsheet,
        roles: ['admin', 'ceo', 'vice-presidente', 'diretor-executivo', 'diretor-financeiro'],
        badge: 'Em Teste'
      },
      {
        name: 'Contratos',
        href: '/financial/contratos',
        icon: FileSignature,
        roles: ['admin', 'ceo', 'vice-presidente', 'diretor-executivo']
      },
      {
        name: 'Parcerias',
        href: '/financial/parcerias',
        icon: HeartHandshake,
        roles: ['admin', 'ceo', 'vice-presidente', 'diretor-executivo'],
        badge: 'V1'
      },
      {
        name: 'Contas a Receber',
        href: '/financial/aging',
        icon: Clock,
        roles: ['admin', 'ceo', 'vice-presidente', 'diretor-executivo', 'diretor-financeiro'],
        badge: 'Em Teste'
      },
      {
        name: 'Notas BB',
        href: '/financial/notas-bb',
        icon: FileText,
        roles: ['admin', 'ceo', 'vice-presidente', 'diretor-executivo', 'diretor-financeiro'],
        badge: 'Protótipo'
      }
    ]
  },
  {
    title: 'Desempenho',
    roles: ['admin', 'diretor-tecnico', 'diretor-executivo', 'diretor-financeiro', 'vice-presidente', 'coordenador', 'rh', 'analista-rh'],
    items: [
      {
        name: 'Resumo Geral',
        href: '/performance',
        icon: BarChart3,
        roles: ['admin']
      },
      {
        name: 'Feedbacks',
        href: '/performance/feedbacks',
        icon: MessageSquare,
        roles: ['admin', 'diretor-tecnico', 'diretor-executivo', 'diretor-financeiro', 'vice-presidente', 'coordenador', 'rh', 'analista-rh']
      },
      {
        name: 'Avaliação de Desempenho',
        href: '/performance/avaliacoes',
        icon: ClipboardList,
        roles: ['admin', 'diretor-tecnico', 'diretor-executivo', 'diretor-financeiro', 'vice-presidente', 'rh', 'analista-rh']
      },
      {
        name: 'Avaliação Individual',
        href: '/performance/individual',
        icon: UserCheck,
        roles: ['admin', 'diretor-tecnico', 'diretor-executivo', 'diretor-financeiro', 'vice-presidente', 'coordenador', 'rh', 'analista-rh']
      },
      {
        name: 'Todas as Avaliações',
        href: '/performance/avaliacoes-individuais',
        icon: Users,
        roles: ['admin', 'vice-presidente', 'rh']
      },
      {
        name: 'Objetivos Individuais',
        href: '/performance/objetivos-individuais',
        icon: Target,
        roles: ['admin', 'vice-presidente', 'rh']
      },
      {
        name: 'Radar de Desempenho',
        href: '/performance/radar',
        icon: Radar,
        roles: ['admin', 'ceo', 'vice-presidente', 'diretor-executivo', 'diretor-financeiro', 'rh', 'analista-rh']
      },
      {
        name: 'Pesquisa de Clima',
        href: '/performance/clima',
        icon: Heart,
        roles: ['admin'] // Only admin can see Pesquisa de Clima
      },
      {
        name: 'Bonificação',
        href: '/performance/bonificacao',
        icon: Trophy,
        roles: ['admin']
      },
      {
        name: 'Configurações',
        href: '/performance/configuracoes',
        icon: Settings,
        roles: ['admin', 'rh', 'analista-rh']
      }
    ]
  },
  {
    title: 'Equipe',
    roles: ['admin', 'diretor-executivo', 'diretor-financeiro', 'diretor-tecnico', 'coordenador', 'rh', 'analista-rh'],
    items: [
      // HIDDEN: Colaboradores menu item temporarily disabled
      // {
      //   name: 'Colaboradores',
      //   href: '/employees',
      //   icon: Users,
      //   roles: ['admin', 'diretor-tecnico']
      // },
      {
        name: 'Painel de Colaboradores',
        href: '/painel-colaboradores',
        icon: BarChart3,
        roles: ['admin', 'rh', 'diretor-tecnico', 'diretor-financeiro']
      },
      {
        name: 'Minha Equipe',
        href: '/my-team',
        icon: UsersRound,
        roles: ['admin', 'diretor-executivo', 'diretor-financeiro', 'diretor-tecnico', 'coordenador', 'rh', 'analista-rh']
      },
      {
        name: 'Férias',
        href: '/vacation',
        icon: Plane,
        roles: ['admin']
      },
      {
        name: 'Calendário',
        href: '/calendar',
        icon: Calendar,
        roles: ['admin'] // Only admin can see Calendário
      },
      {
        name: 'Onboarding',
        href: '/onboarding',
        icon: BookOpen,
        roles: ['admin'] // Only admin can see Onboarding
      }
    ]
  },
  {
    title: 'Capacitação',
    roles: ['admin', 'colaborador', 'diretor-tecnico', 'coordenador', 'rh', 'analista-rh'],
    items: [
      {
        name: 'Meus cursos',
        href: '/my-courses',
        icon: BookOpen,
        roles: ['admin', 'colaborador', 'diretor-tecnico', 'coordenador', 'rh', 'analista-rh']
      },
      {
        name: 'Cursos da Equipe',
        href: '/team-courses',
        icon: BookOpen,
        roles: ['admin', 'diretor-tecnico', 'coordenador']
      }
    ]
  },
  {
    title: 'Gestão de Pessoas',
    roles: ['admin', 'rh', 'analista-rh'],
    items: [
      // {
      //   name: 'Dashboard',
      //   href: '/workforce',
      //   icon: Users,
      //   roles: ['admin', 'rh', 'analista-rh']
      // },
      {
        name: 'Colaboradores',
        href: '/workforce/employees',
        icon: Users,
        roles: ['admin', 'rh', 'analista-rh']
      },
      {
        name: 'Departamentos',
        href: '/workforce/departments',
        icon: Building2,
        roles: ['admin', 'rh', 'analista-rh']
      },
      {
        name: 'Cargos',
        href: '/workforce/roles',
        icon: Briefcase,
        roles: ['admin', 'rh', 'analista-rh']
      }
    ]
  },
  // {
  //   title: 'Tempo & Benefícios',
  //   roles: ['admin', 'employee'],
  //   items: [
  //     {
  //       name: 'Férias',
  //       href: '/vacation',
  //       icon: Calendar,
  //       roles: ['admin', 'employee']
  //     }
  //   ]
  // },
  // {
  //   title: 'Desenvolvimento',
  //   roles: ['admin', 'employee'],
  //   items: [
  //     {
  //       name: 'Treinamentos',
  //       href: '/training',
  //       icon: Award,
  //       roles: ['admin', 'employee']
  //     }
  //   ]
  // },
]

export default function Sidebar({ isOpen, onClose, user, onJanesChatbotOpen }: SidebarProps) {
  const router = useRouterState()
  const pathname = router.location.pathname
  // Filter sections and items based on user role
  const filteredSections = navigationSections
    .map(section => ({
      ...section,
      items: section.items.filter(item => item.roles.includes(user?.role || ''))
    }))
    .filter(section =>
      section.roles.includes(user?.role || '') && section.items.length > 0
    )

  const { isOpen: isExpanded, toggleSidebar } = useSidebarStore()
  const collapsed = !isExpanded

  return (
    <div
      className={`
        ${collapsed ? 'w-20' : 'w-64'} bg-brand-dark-light flex-shrink-0
        fixed inset-y-0 left-0 transform transition-all duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0 lg:relative lg:inset-0 lg:block
      `}
      style={{ zIndex: Z_INDEX.SIDEBAR }}
    >
      <div className="flex flex-col h-full">
        {/* Logo */}
        <div className={`flex items-center pt-[39px] pb-4 transition-all duration-300 ${collapsed ? 'justify-center px-3' : 'justify-between px-6'}`}>
          <div className={`flex items-center min-w-0 ${collapsed ? 'gap-0' : 'gap-2.5'}`}>
            <img src="/assets/brand/logos/monumenta-logo-yellow.png" alt="Monumenta" className="h-8 w-8 object-contain flex-shrink-0" />
            <h1
              className={`font-display text-xl sm:text-2xl font-bold tracking-wider whitespace-nowrap overflow-hidden transition-all duration-300 ${
                collapsed ? 'w-0 opacity-0' : 'w-auto opacity-100'
              }`}
            >
              <span className="text-white">MONU</span><span className="text-brand-primary font-normal">TRACK</span>
            </h1>
          </div>
          {!collapsed && (
            <button
              onClick={onClose}
              className="lg:hidden text-gray-400 hover:text-white flex-shrink-0"
            >
              <X size={24} strokeWidth={1.5} />
            </button>
          )}
        </div>

        {/* Divider + Collapse Toggle */}
        <div className={`${collapsed ? 'mx-3' : 'mx-6'} relative flex items-center`}>
          <div className="flex-1 border-b border-brand-dark-deepest" />
          <button
            onClick={toggleSidebar}
            className="hidden lg:flex absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-brand-dark-light border border-brand-dark-deepest items-center justify-center text-gray-400 hover:text-white hover:border-gray-500 transition-colors z-10"
          >
            {collapsed ? (
              <ChevronsRight size={14} strokeWidth={1.5} />
            ) : (
              <ChevronsLeft size={14} strokeWidth={1.5} />
            )}
          </button>
        </div>
        <div className="pt-4" />

        {/* Manu Chatbot Button - Prominent at top - Only for CEO */}
        {user?.role === 'ceo' && (
          <div className={`${collapsed ? 'px-3' : 'px-4'} pt-4 pb-2`}>
            <button
              onClick={onJanesChatbotOpen}
              className={`flex items-center ${collapsed ? 'justify-center' : ''} w-full ${collapsed ? 'px-0 py-3' : 'px-4 py-3'} text-sm font-medium rounded-lg transition-all duration-200 bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105`}
              title={collapsed ? 'Pergunta pra Manu' : undefined}
            >
              <MessageCircle size={20} strokeWidth={1.5} className={collapsed ? '' : 'mr-3'} />
              {!collapsed && <span className="truncate font-semibold">Pergunta pra Manu</span>}
            </button>
          </div>
        )}

        {/* Navigation */}
        <nav className={`flex-1 ${collapsed ? 'px-3' : 'px-4'} py-2 space-y-6 overflow-y-auto`}>
          <TooltipProvider delayDuration={0}>
          {filteredSections.map((section, sectionIndex) => (
            <div key={section.title} className="space-y-2">
              {/* Section Header */}
              {sectionIndex === 0 ? null : collapsed ? (
                <div className="mx-2 border-b border-brand-primary/15" />
              ) : (
                <h3 className="px-3 text-xs font-semibold text-brand-primary/50 uppercase tracking-wider">
                  {section.title}
                </h3>
              )}

              {/* Section Items */}
              <div className="space-y-1">
                {section.items.map((item) => {
                  const Icon = item.icon
                  const isActive = pathname === item.href
                  const isComingSoon = item.comingSoon

                  // Render as disabled button if coming soon
                  if (isComingSoon) {
                    const comingSoonContent = (
                      <div
                        className={`
                          flex items-center ${collapsed ? 'justify-center px-0' : 'px-3'} py-2 text-sm font-medium rounded-lg transition-all duration-200
                          opacity-50 cursor-not-allowed relative
                          ${isActive ? 'bg-brand-primary/5' : ''}
                        `}
                        aria-label={`${item.name} - Em breve`}
                      >
                        <Icon size={18} strokeWidth={1.5} className={`flex-shrink-0 text-gray-500 ${collapsed ? '' : 'mr-3'}`} />
                        {!collapsed && (
                          <>
                            <span className="truncate text-gray-500">{item.name}</span>
                            <Clock size={12} strokeWidth={1.5} className="ml-auto flex-shrink-0 text-amber-500" aria-hidden="true" />
                          </>
                        )}
                      </div>
                    )

                    return (
                      <Tooltip key={item.name}>
                        <TooltipTrigger asChild>
                          {comingSoonContent}
                        </TooltipTrigger>
                        <TooltipContent side="right">
                          <p>{item.name} - Em breve</p>
                        </TooltipContent>
                      </Tooltip>
                    )
                  }

                  const linkContent = (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`
                        relative flex items-center ${collapsed ? 'justify-center px-0' : 'px-3'} py-2.5 text-sm font-medium rounded-lg transition-all duration-200
                        ${isActive
                          ? 'bg-brand-dark-deepest text-white'
                          : 'text-gray-400 hover:bg-brand-dark-deepest/50 hover:text-white'
                        }
                      `}
                      onClick={onClose}
                    >
                      {isActive && (
                        <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 bg-brand-primary rounded-r-full" />
                      )}
                      <Icon size={18} strokeWidth={1.5} className={`flex-shrink-0 ${isActive ? 'text-brand-primary' : ''} ${collapsed ? '' : 'mr-3'}`} />
                      {!collapsed && (
                        <>
                          <span className="truncate">{item.name}</span>
                          {item.badge && (
                            <span className="ml-auto text-[10px] font-normal text-gray-500 border border-gray-600 px-1.5 py-0.5 rounded">
                              {item.badge}
                            </span>
                          )}
                        </>
                      )}
                    </Link>
                  )

                  if (collapsed) {
                    return (
                      <Tooltip key={item.name}>
                        <TooltipTrigger asChild>
                          {linkContent}
                        </TooltipTrigger>
                        <TooltipContent side="right">
                          <p>{item.name}</p>
                        </TooltipContent>
                      </Tooltip>
                    )
                  }

                  return linkContent
                })}
              </div>
            </div>
          ))}
          </TooltipProvider>
        </nav>

        {/* User Info */}
        {!collapsed && <UserInfo user={user} />}
      </div>
    </div>
  )
}